import {
  createAsyncThunk,
  ThunkDispatch,
  UnknownAction,
} from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { toMerged } from 'es-toolkit';
import { omit } from 'es-toolkit/compat';
import { JSZipObject, loadAsync } from 'jszip';
import { DeepPartial } from 'react-hook-form';

import { CONSTANTS } from '@/config/constants';
import {
  DEFAULT_GEOMETRY_CLASS_DEFINITION,
  DEFAULT_GEOMETRY_INSTANCE,
} from '@/config/defaults';
import { projectsApi } from '@/lib/apis';

import { GeometryClassDefinition } from '../../../custom-shared/types/editor/classes';
import { ProjectMetadata } from '../../../custom-shared/types/editor/common';
import { GeometryClassInstance } from '../../../custom-shared/types/editor/instances';
import {
  ALLOWED_PROJECT_EXTENSIONS,
  GeometryExtensions,
  JSONProjectContent,
  JSONProjectFile,
  METADATA_ALLOWED_EXTENSIONS,
  MetadataExtensions,
  ObjectURLProjectFile,
  ProjectFileExtensions,
} from '../../../custom-shared/types/project';
import { needsUpdatedAt } from '../../utils/project-files';
import { setEditorLoading } from '../ui/editor-ui.actions';

import { RootState } from '@/stores/store';
import { CustomGeometryClassNames } from '@/types/custom';
import { i18n } from '@nablax/i18n';
import { ProjectResponse } from '@nablax/sdk';
import { logger } from '@nablax/shared/lib';
import { alert, selectUserId } from '@nablax/shared/stores';
import { FileImportError, ProjectLoadError } from '@nablax/shared/types';
import {
  basename,
  extname,
  noCacheParams,
  pLimit,
  safeParseJSON,
  safeParseJSONFile,
  set,
} from '@nablax/shared/utils';
import {
  selectAllSTLFiles,
  selectDefinitionJSONFileTyped,
  selectProject,
  selectProjectJSONFile,
} from './project.selectors';
import { ProjectFilesTree } from './types';

export type LoadLocalProjectAction = {
  type: 'local';
  projectId: string;
  files: FileList;
};
export type LoadCloudProjectAction = { type: 'cloud'; projectId: string };

export const loadProject = createAsyncThunk<
  null,
  LoadLocalProjectAction | LoadCloudProjectAction
>('project/load', async (action, { dispatch }) => {
  if (action.type === 'local') {
    dispatch(loadLocalProject(action));
  } else if (action.type === 'cloud') {
    dispatch(loadCloudProject(action));
  }

  return null;
});

export const loadLocalProject = createAsyncThunk<
  ProjectFilesTree,
  LoadLocalProjectAction
>(
  'project/load:local',
  async ({ files, projectId }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const stlFiles = selectAllSTLFiles(state);
    const userId = selectUserId(state)!;

    revokePreviousFiles(stlFiles);

    const tree = await storeLocalFileList(projectId, userId, files, dispatch);
    return tree;
  },
);

export const loadCloudProject = createAsyncThunk<
  ProjectFilesTree,
  LoadCloudProjectAction
>('project/load:cloud', async ({ projectId }, { getState, dispatch }) => {
  const state = getState() as RootState;
  const stlFiles = selectAllSTLFiles(state);

  revokePreviousFiles(stlFiles);

  const { data: project } = await projectsApi.projectsGet(projectId);

  const tree = await storeCloudZipBundle(project, dispatch);
  return tree;
});

export const refreshProjectFile = createAsyncThunk<
  Partial<ProjectFilesTree>,
  | { type: 'instance'; baseName: string; exts: ProjectFileExtensions[] }
  | { type: 'definition'; geometryClass: string; exts: ['json'] }
>('project/refresh:file', async (action, { getState, dispatch }) => {
  const { type } = action;
  const state = getState() as RootState;
  const project = selectProject(state);

  if (!project) {
    return {};
  }

  const fileNames: string[] = [];
  for (const ext of action.exts) {
    fileNames.push(
      type === 'definition'
        ? `${action.geometryClass}.definition.${ext}`
        : `${action.baseName}.${ext}`,
    );
  }

  const tree: ProjectFilesTree = {
    project: null,
    definitionFiles: {},
    files: {},
  };
  for (const fileName of fileNames) {
    await storeCloudFile(project, fileName, tree, dispatch, true);
  }

  return tree;
});

function revokePreviousFiles(files: ObjectURLProjectFile[]) {
  for (const { objectURL } of files || []) {
    URL.revokeObjectURL(objectURL);
  }
}

async function storeLocalFileList(
  projectId: string,
  userId: string,
  files: FileList,
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
) {
  let tree: ProjectFilesTree = {
    project: null,
    files: {},
    definitionFiles: {},
  };

  await Promise.all(
    Array.from(files || []).map(async (file) => {
      try {
        const ext = extname(file.name);
        if (ext === 'json') {
          await storeJSON(file, tree, {
            userId,
            id: projectId,
          });
        } else if (ext === 'stl') {
          storeSTL(file.name, file, tree);
        } else {
          logger.error('project: unrecognized file for project', file);
          throw new FileImportError('alerts.invalidFile', file.name);
        }
      } catch (e) {
        if (e instanceof FileImportError)
          dispatch(
            alert({
              variant: 'warning',
              title: 'alerts.warning',
              message: e.message,
              data: { name: e.fileName },
            }),
          );
      }
    }),
  );

  tree = injectDefaults(tree, {
    userId,
    id: projectId,
  });
  tree = validateTree(tree);
  return tree;
}

async function storeCloudZipBundle(
  project: ProjectResponse,
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
) {
  let tree: ProjectFilesTree = {
    project,
    files: {},
    definitionFiles: {},
  };

  const response = await projectsApi.projectsZipGet(project.id, {
    responseType: 'blob',
  });

  const { data: blob } = response;
  const zip = await loadAsync(blob as any);
  const files: JSZipObject[] = [];

  for (const baseName in zip.files) {
    if (Object.prototype.hasOwnProperty.call(zip.files, baseName)) {
      const file = zip.files[baseName];
      files.push(file);
    }
  }

  const promises = files.map((file) =>
    storeCloudFileZip.bind({}, file, tree, dispatch),
  );

  await pLimit(promises, CONSTANTS.projects.concurrentDownload);

  tree = injectDefaults(tree);
  tree = validateTree(tree);
  return tree;
}

async function storeCloudFileZip(
  file: JSZipObject,
  tree: ProjectFilesTree,
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
) {
  dispatch(setEditorLoading({ project: file.name }));

  const fileName = file.name;
  const ext = extname(file.name);
  try {
    if (METADATA_ALLOWED_EXTENSIONS.includes(ext as 'json')) {
      // json file handling
      const blob = await file.async('blob');
      const text = await blob.text();
      const content = safeParseJSON<
        GeometryClassDefinition | GeometryClassInstance
      >(text);

      if (!content) {
        throw new FileImportError('alerts.invalidFile', fileName);
      }

      await storeJSON(
        {
          name: fileName,
          content,
        },
        tree,
      );
    } else if (ALLOWED_PROJECT_EXTENSIONS.includes(ext as GeometryExtensions)) {
      // stl files handling
      const blob = await file.async('blob');
      storeSTL(fileName, blob, tree);
    } else {
      throw new FileImportError('alerts.invalidFile', fileName);
    }
  } catch (e: any) {
    if (e instanceof FileImportError)
      dispatch(
        alert({
          variant: 'warning',
          title: 'alerts.warning',
          message: e.message,
          data: { name: fileName },
        }),
      );
  }
}

async function storeCloudFile(
  project: ProjectResponse,
  fileName: string,
  tree: ProjectFilesTree,
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  invalidateCache: boolean = false,
) {
  dispatch(setEditorLoading({ project: fileName }));
  const response = await projectsApi.projectsFilesGet(project.id, fileName, {
    responseType: fileName.endsWith('.json') ? 'json' : 'blob',
    params: invalidateCache ? noCacheParams() : null,
  });

  const ext = extname(fileName);
  try {
    if (METADATA_ALLOWED_EXTENSIONS.includes(ext as MetadataExtensions)) {
      // json file handling
      const content = response.data as any;
      await storeJSON(
        {
          name: fileName,
          content,
        },
        tree,
      );
    } else if (ALLOWED_PROJECT_EXTENSIONS.includes(ext as GeometryExtensions)) {
      // stl files handling
      storeSTL(fileName, response, tree);
    } else {
      throw new FileImportError('alerts.invalidFile', fileName);
    }
  } catch (e: any) {
    if (e instanceof FileImportError)
      dispatch(
        alert({
          variant: 'warning',
          title: 'alerts.warning',
          message: e.message,
          data: { name: fileName },
        }),
      );
  }
}

/**
 * Store a json file inside the passed tree.
 * Works both with local uploaded files or cloud file
 * @param file File if local, {name, content} if from cloud
 * @param tree tree to update
 * @param localProjectDetails only for local project, additional data for parsing project metadata.json file
 */
async function storeJSON(
  file:
    | File
    | {
        name: string;
        content:
          | ProjectMetadata
          | GeometryClassDefinition
          | GeometryClassInstance;
      },
  tree: ProjectFilesTree,
  localProjectDetails: { id: string; userId: string } | null = null,
) {
  let content:
    | ProjectMetadata
    | GeometryClassDefinition
    | GeometryClassInstance
    | null = null;

  if (typeof file === 'object' && 'text' in file) {
    content = await safeParseJSONFile<
      ProjectMetadata | GeometryClassDefinition | GeometryClassInstance
    >(file);
  } else {
    content = file.content;
  }

  if (!content) {
    throw new FileImportError('alerts.invalidJson', file.name);
  }

  const { name: fullName } = file;
  const baseName = basename(file.name);
  const ext = extname(file.name) as 'json' | 'stl';

  // compatibility with local projects metadata.json
  const isProjectMetadata = fullName === 'metadata.json';
  const isDefinition = fullName.endsWith('.definition.json');

  if (isDefinition && 'name' in content) {
    const { name: className } = content;
    set<JSONProjectFile<GeometryClassDefinition>>(
      tree,
      `definitionFiles.${className}`,
      {
        type: 'json',
        baseName,
        ext,
        fullName,
        content: content,
      },
    );
  } else if ('className' in content) {
    set<JSONProjectFile<GeometryClassInstance>>(
      tree,
      `files.${baseName}.json`,
      {
        type: 'json',
        baseName,
        ext,
        fullName,
        content,
      },
    );
  } else if (
    isProjectMetadata &&
    'projectName' in content &&
    localProjectDetails
  ) {
    tree.project = {
      id: localProjectDetails.id,
      software_name: CONSTANTS.tasks.softwareName,
      name: content.projectName,
      user_id: localProjectDetails.userId,
      updated_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      files: [],
    };
  } else {
    throw new FileImportError('alerts.invalidJson', file.name);
  }
}

/**
 * Use this function to store a STL file (both from cloud or local)
 * inside the passed tree
 * @param fullName file full name
 * @param blobOrResponse if local File, else AxiosResponse from cloud
 * @param tree tree to update
 */
function storeSTL(
  fullName: string,
  blobOrResponse: Blob | AxiosResponse,
  tree: ProjectFilesTree,
) {
  const baseName = basename(fullName);
  const ext = extname(fullName) as 'stl' | 'jpg';

  let objectURL: string;
  if ('status' in blobOrResponse) {
    const { data: blob } = blobOrResponse;
    objectURL = URL.createObjectURL(blob as Blob);
  } else {
    objectURL = URL.createObjectURL(blobOrResponse);
  }

  set<ObjectURLProjectFile>(tree, `files.${baseName}.${ext}`, {
    type: ext,
    baseName,
    ext,
    fullName,
    objectURL,
  });
}

/**
 * Ensure that default geometry class definitions are injected inside the
 * passed tree
 * @param tree tree to inject
 * @param localProjectDetails pass other project details only for local projects
 * @returns
 */
function injectDefaults(
  tree: ProjectFilesTree,
  localProjectDetails: { id: string; userId: string } | null = null,
) {
  // create default definition file
  if (
    !Object.keys(tree.definitionFiles).length ||
    !tree.definitionFiles[DEFAULT_GEOMETRY_CLASS_DEFINITION.name]
  ) {
    tree.definitionFiles[DEFAULT_GEOMETRY_CLASS_DEFINITION.name] = {
      baseName: DEFAULT_GEOMETRY_CLASS_DEFINITION.name,
      fullName: `${DEFAULT_GEOMETRY_CLASS_DEFINITION.name}.definition.json`,
      content: DEFAULT_GEOMETRY_CLASS_DEFINITION,
      type: 'json',
      ext: 'json',
    };
  }

  // create default uncategorized json for each missing json instance file
  for (const baseName in tree.files) {
    if (!tree.files[baseName]!.json) {
      tree.files[baseName]!.json = {
        type: 'json',
        baseName,
        content: DEFAULT_GEOMETRY_INSTANCE,
        ext: 'json',
        fullName: `${baseName}.json`,
      };
    }
  }

  if (localProjectDetails && !tree.project) {
    tree.project = {
      id: localProjectDetails.id,
      software_name: CONSTANTS.tasks.softwareName,
      name: i18n.t('editor.sidebar.defaultSubtitle'),
      user_id: localProjectDetails.userId,
      updated_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      files: [],
    };
  }

  return tree;
}

/**
 * This function checks whenever a tree calculated from a project
 * is valid or not. If there are no STL files the project is considered
 * invalid.
 * @param tree tree to parse
 * @returns tree validated
 */
function validateTree(tree: ProjectFilesTree): ProjectFilesTree {
  const entries = Object.entries(tree.files);

  for (const [key, data] of entries) {
    if (!data || !data.stl) {
      continue;
    }

    return tree;
  }

  throw new ProjectLoadError('alerts.invalidprojectnostl');
}

/**
 * Use this action to patch JSON project files.
 * You can specify a `patchContent` that will be merged
 * with the original json.
 * You can also specify a `removeKeys` prop to remove
 * keys from the original json, these keys can be object paths
 * @example { removeKeys: ['displayProperties.material.color'] }
 */
export const patchProjectFile = createAsyncThunk<
  {
    type: 'instance' | 'definition';
    baseName: string;
    content: JSONProjectContent;
  } | null,
  {
    type: 'instance' | 'definition';
    baseName: string;
    patchContent?: DeepPartial<JSONProjectContent>;
    removeKeys?: string[];
  }
>(
  'project/patchFile',
  async ({ type, baseName, patchContent, removeKeys }, { getState }) => {
    const state = getState() as RootState;

    let currentFile: JSONProjectFile | null = null;
    if (type === 'definition') {
      currentFile = selectDefinitionJSONFileTyped()(
        state,
        baseName as CustomGeometryClassNames,
      );
    } else {
      currentFile = selectProjectJSONFile(state, baseName);
    }

    if (!currentFile || (!patchContent && !removeKeys)) {
      return null;
    }

    const { content } = currentFile;
    let result = toMerged(content, patchContent || {});
    // FIXME: performance wise deep omit is not the best
    result = omit(
      result,
      removeKeys as Array<keyof typeof result>,
    ) as JSONProjectContent;
    if (needsUpdatedAt(patchContent, removeKeys))
      result.updatedAt = new Date().toISOString();

    return {
      type,
      baseName,
      content: result,
    };
  },
);
